/**
 * The two residence questions — the country the citizen is moving from and
 * the city they live in — for `useResidence`'s form. Rendered inside the
 * invitation review, the accept page and a journey still waiting to open.
 */
import { useCountries, useTranslation } from '@tonnie/locale';
import { cn, CountrySelect, Label } from '@tonnie/ui';
import type { TFunction } from 'i18next';
import { Controller } from 'react-hook-form';

import { CityAutocomplete } from '@/components/ui/CityAutocomplete';

import type { ResidenceState } from '../hooks/use-residence';

/**
 * The schema's messages are i18n keys (`citizenSetupSchema`). Each is rendered
 * through a LITERAL `t()` so the key-usage gate can prove every one exists; an
 * unknown message renders the generic "required", never a raw key.
 */
function fieldErrorText(t: TFunction, message: string): string {
  switch (message) {
    case 'validation.originCountry.required':
      return t('validation.originCountry.required');
    case 'validation.countryCode.length':
      return t('validation.countryCode.length');
    case 'validation.city.required':
      return t('validation.city.required');
    case 'validation.city.tooLong':
      return t('validation.city.tooLong');
    default:
      return t('validation.required');
  }
}

export function ResidenceFields({
  residence,
  idPrefix,
  disabled = false,
}: {
  residence: ResidenceState;
  /** Unique per surface, so two forms on one page never share an id. */
  idPrefix: string;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const { countries } = useCountries();
  const {
    control,
    watch,
    formState: { errors },
  } = residence.form;
  const country = watch('currentCountry');
  const busy = disabled || residence.isSaving;

  return (
    <div className="space-y-4" data-testid={`${idPrefix}-residence`}>
      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-country`} className="text-sm font-medium">
          {t('setup.countryLabel')}{' '}
          <span className="text-destructive-accessible" aria-hidden="true">
            *
          </span>
        </Label>
        <Controller
          control={control}
          name="currentCountry"
          render={({ field }) => (
            <CountrySelect
              id={`${idPrefix}-country`}
              data-testid={`${idPrefix}-country`}
              countries={countries}
              value={field.value}
              onValueChange={(code) => {
                field.onChange(code);
                field.onBlur();
              }}
              placeholder={t('setup.countryPlaceholder')}
              searchPlaceholder={t('common.searchCountry')}
              emptyMessage={t('common.noCountryFound')}
              disabled={busy}
              className={cn('h-11', errors.currentCountry && 'border-destructive')}
              aria-invalid={!!errors.currentCountry}
              aria-describedby={errors.currentCountry ? `${idPrefix}-country-error` : undefined}
            />
          )}
        />
        {errors.currentCountry?.message && (
          <p
            id={`${idPrefix}-country-error`}
            className="text-xs text-destructive-accessible"
            role="alert"
          >
            {fieldErrorText(t, errors.currentCountry.message)}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor={`${idPrefix}-city`} className="text-sm font-medium">
          {t('setup.cityLabel')}{' '}
          <span className="text-destructive-accessible" aria-hidden="true">
            *
          </span>
        </Label>
        <Controller
          control={control}
          name="currentCity"
          render={({ field }) => (
            <CityAutocomplete
              id={`${idPrefix}-city`}
              value={field.value}
              onChange={field.onChange}
              onBlur={field.onBlur}
              countryCode={country || null}
              placeholder={t('setup.cityPlaceholder')}
              invalid={!!errors.currentCity}
              describedBy={errors.currentCity ? `${idPrefix}-city-error` : undefined}
              disabled={busy}
              data-testid={`${idPrefix}-city`}
            />
          )}
        />
        {errors.currentCity?.message && (
          <p
            id={`${idPrefix}-city-error`}
            className="text-xs text-destructive-accessible"
            role="alert"
          >
            {fieldErrorText(t, errors.currentCity.message)}
          </p>
        )}
      </div>

      {residence.saveFailed && (
        <p
          className="text-sm text-destructive-accessible"
          role="alert"
          data-testid={`${idPrefix}-residence-error`}
        >
          {t('setup.saveFailed')}
        </p>
      )}
    </div>
  );
}
